import { CountryCode } from '@abiglobalhealth/go-types'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { setCaseNotification } from '@/api/consultations'
import { Button } from '@/components/Button'
import { PhoneNumber } from '@/components/PhoneNumber'
import { useConsultation } from '@/hooks/useConsultation'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import { cn } from '@/utils/tailwind'

import { SUBMIT_STATUS } from '.'

interface PhoneNumberPopupProps {
  submitStatus: SUBMIT_STATUS | null
  setSubmitStatus: (submitStatus: SUBMIT_STATUS) => void
  showInput: boolean
  setShowInput: (showInput: boolean) => void
}

export const PhoneNumberPopup = ({ submitStatus, setSubmitStatus, showInput, setShowInput }: PhoneNumberPopupProps) => {
  const { t } = useTranslation()
  const { consultation, updateConsultation } = useConsultation()
  const { user, updateUser } = useUser()
  const { getFieldError } = useFormErrors()
  const { getUserDataById, setUserUpdatedFields } = useUserForm()
  const phone = getUserDataById('phone') as string

  const containerRef = useRef<HTMLDivElement>(null)

  const handleSubmit = async () => {
    if (getFieldError('phone') || submitStatus === SUBMIT_STATUS.SUBMITTING) return

    setSubmitStatus(SUBMIT_STATUS.SUBMITTING)

    try {
      const { user: updatedUser, consultation: updatedConsultation } = await setCaseNotification({
        uniqueId: user!.data.uniqueId!,
        phone,
        consultationId: consultation?.consultationId!
      })

      updateUser(updatedUser)
      updateConsultation(updatedConsultation)

      setSubmitStatus(SUBMIT_STATUS.SUCCESS)
    } catch (e) {
      console.error(e)
      setSubmitStatus(SUBMIT_STATUS.ERROR)
    } finally {
      setUserUpdatedFields([])
    }
  }

  const handleShowInput = () => {
    setShowInput(true)

    // Focus on phone input
    setTimeout(() => {
      const input = document.getElementById('phone')
      input?.focus()
    }, 100)
  }

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!showInput || !containerRef?.current) return

      // Check if the click was outside the container
      if (!containerRef.current.contains(event.target as Node)) {
        setShowInput(false)
      }
    },
    [showInput, containerRef]
  )

  useEffect(() => {
    if (!showInput) return setUserUpdatedFields([])
  }, [showInput])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  return (
    <div ref={containerRef} className="flex w-full flex-col p-4">
      <div
        className="w-full transition-[max-height] duration-500"
        style={{
          maxHeight: showInput ? '8rem' : 0
        }}
        data-testid="phone-number-outer-container"
      >
        {showInput && (
          <PhoneNumber
            id="phone"
            required
            tabIndex={0}
            defaultCountry={user?.data.country?.code?.toUpperCase()?.split('-')[0] as CountryCode}
            value={phone}
            textKey="inputs.phone.label"
            containerClassName={cn(showInput ? 'animate-fade-in' : 'animate-fade-out')}
          />
        )}
      </div>

      <Button
        customClassName="bg-primary z-10 text-white"
        custom
        onClick={showInput ? handleSubmit : handleShowInput}
        loading={submitStatus === SUBMIT_STATUS.SUBMITTING}
        hasShadow={false}
      >
        {t(showInput ? 'button.submit' : 'button.sms_reminder')}
      </Button>
    </div>
  )
}
